import React, { useRef, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartIconSolid
} from '@heroicons/react/24/solid';
import { itinerariesApi } from '../services/api';
import { getUserId } from '../utils/userId';
import DesignTokens from '../styles/DesignTokens';
import {
  CountrySection,
  CountrySectionHeader,
  CountryTitle,
  ItineraryCarouselContainer,
  CarouselPrevButton,
  CarouselNextButton,
  ItineraryCarouselCardsContainer,
  ItineraryCarouselCard,
  ItineraryImage,
  AIBadgeSmall,
  ItineraryContent,
  ItineraryCardTitle,
  ItineraryCardDescription,
  ItineraryCardMeta
} from './ItineraryPage.styles';

// Component for country carousel that can fetch itineraries (shown when country is expanded)
const CountryItinerariesCarousel = ({ country, countryIndex, carouselRefs, setSearchParams, navigate, setSelectedCountryFilter, setFormData, generateItineraryTagline, onClose }) => {
  const countryItinerariesRef = useRef(null);
  const userId = getUserId();

  // Normalize country name (trim and ensure consistent format)
  const normalizedCountry = country ? country.trim() : null;
  
  // Fetch itineraries for this country
  const { data: fetchedItineraries, isLoading: isLoadingCountryItineraries, error: countryItinerariesError } = useQuery(
    ['countryItineraries', normalizedCountry, userId],
    () => {
      if (!normalizedCountry) {
        return Promise.resolve([]);
      }
      return itinerariesApi.getByCountry(normalizedCountry, userId);
    },
    {
      enabled: !!normalizedCountry,
      select: (response) => {
        // Handle both axios response format and direct array
        let data = response;
        if (response && typeof response === 'object' && 'data' in response) {
          data = response.data;
        }
        const itineraries = Array.isArray(data) ? data : [];
        return itineraries;
      },
      retry: 2,
      staleTime: 2 * 60 * 1000, // Reduced from 5 minutes to 2 minutes
      cacheTime: 5 * 60 * 1000,
      onError: (error) => {
        // Error fetching itineraries for country - removed for production
      }
    }
  );
  
  // Log error if present
  useEffect(() => {
    if (countryItinerariesError) {
      // Country itineraries query error
    }
  }, [countryItinerariesError]);
  
  
  // Memoize displayItineraries to prevent unnecessary re-renders
  const displayItineraries = useMemo(() => {
    return fetchedItineraries || [];
  }, [fetchedItineraries]);
  
  // Data fetched for country carousel - removed debug logging for production
  
  const scrollAmount = 340; // Card width + gap
  
  const handleCarouselPrev = () => {
    const container = countryItinerariesRef.current;
    if (container) {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };
  
  const handleCarouselNext = () => {
    const container = countryItinerariesRef.current;
    if (container) {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };
  
  const canScrollPrev = () => {
    const container = countryItinerariesRef.current;
    if (!container) return false;
    return container.scrollLeft > 0;
  };
  
  const canScrollNext = () => {
    const container = countryItinerariesRef.current;
    if (!container) return false;
    return container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
  };
  
  return (
    <CountrySection>
      <CountrySectionHeader>
        <div>
          <CountryTitle>
            <MapPinIcon style={{ width: '24px', height: '24px', color: 'rgb(215 147 251)' }} />
            {country} Itineraries
          </CountryTitle>
        </div>
        {onClose && (
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: `${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm}`,
              background: DesignTokens.colors?.primaryLight || '#E8F0FE',
              color: DesignTokens.colors?.primary || '#4285F4',
              border: 'none',
              borderRadius: DesignTokens.borderRadius.md,
              cursor: 'pointer',
              fontSize: DesignTokens.typography.fontSize.sm,
              fontWeight: DesignTokens.typography.fontWeight.medium
            }}
          >
            Close
          </motion.button>
        )}
      </CountrySectionHeader>
      
      {isLoadingCountryItineraries ? (
        <div style={{ padding: DesignTokens.spacing.xl, textAlign: 'center', color: DesignTokens.colors?.textSecondary }}>
          Loading itineraries...
        </div>
      ) : (
        <ItineraryCarouselContainer>
          {displayItineraries.length > 0 && (
            <CarouselPrevButton
              onClick={handleCarouselPrev}
              disabled={!canScrollPrev()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Previous itineraries"
            >
              <ChevronLeftIcon />
            </CarouselPrevButton>
          )}
          
          <ItineraryCarouselCardsContainer ref={countryItinerariesRef}>
            {displayItineraries.length > 0 ? (
              displayItineraries.map((itinerary, index) => {
                const tagline = generateItineraryTagline(itinerary);
                const countryImage = itinerary.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (index * 1000)}?w=800&q=80&auto=format&fit=crop`;
                
                return (
                  <ItineraryCarouselCard
                    key={itinerary.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -4 }}
                    onClick={() => {
                      const url = itinerary.slug || itinerary.id;
                      navigate(`/itineraries/${url}`, { replace: true });
                    }}
                  >
                    <ItineraryImage imageUrl={countryImage}>
                      {itinerary.itineraryType && (
                        <AIBadgeSmall>
                          <SparklesIcon style={{ width: '12px', height: '12px' }} />
                          {itinerary.itineraryType === 'culture' || itinerary.itineraryType === 'cultural' ? 'Cultural' :
                           itinerary.itineraryType === 'culinary' ? 'Culinary' :
                           itinerary.itineraryType === 'gastronomy' ? 'Gastronomy' :
                           itinerary.itineraryType === 'romantic' ? 'Romantic' :
                           itinerary.itineraryType === 'adventure' ? 'Adventure' :
                           itinerary.itineraryType === 'party' ? 'Party' :
                           itinerary.itineraryType === 'peace' ? 'Peaceful' :
                           itinerary.itineraryType === 'relax' ? 'Relax' :
                           itinerary.itineraryType === 'family' ? 'Family' :
                           itinerary.itineraryType === 'solo' ? 'Solo' :
                           itinerary.itineraryType === 'nature' ? 'Nature' :
                           itinerary.itineraryType === 'historical' ? 'Historical' :
                           itinerary.itineraryType === 'beach' ? 'Beach' :
                           itinerary.itineraryType === 'mountain' ? 'Mountain' :
                           itinerary.itineraryType.charAt(0).toUpperCase() + itinerary.itineraryType.slice(1)}
                        </AIBadgeSmall>
                      )}
                    </ItineraryImage>
                    <ItineraryContent>
                      <ItineraryCardTitle>{tagline}</ItineraryCardTitle>
                      <ItineraryCardDescription>
                        {itinerary.description || itinerary.tripSummary || `Explore ${country} with this curated itinerary.`}
                      </ItineraryCardDescription>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <ItineraryCardMeta>
                          <CalendarDaysIcon style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px' }} />
                          {itinerary.durationDays || itinerary.days || 7} days
                          {' • '}
                          <CurrencyDollarIcon style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px' }} />
                          {itinerary.budgetRange || 'mid-range'}
                          {itinerary.updatedAt && (
                            <>
                              {' • '}
                              <ClockIcon style={{ width: '14px', height: '14px', display: 'inline', marginRight: '4px' }} />
                              Updated {new Date(itinerary.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </>
                          )}
                        </ItineraryCardMeta>
                        {/* Read-only favorite indicator in carousel */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: DesignTokens.spacing.xs,
                          color: itinerary.isFavoritedByUser ? '#EF4444' : DesignTokens.colors?.textTertiary || '#666',
                          fontSize: DesignTokens.typography.fontSize.xs
                        }}>
                          {itinerary.isFavoritedByUser ? (
                            <HeartIconSolid style={{ width: '16px', height: '16px' }} />
                          ) : (
                            <HeartIcon style={{ width: '16px', height: '16px' }} />
                          )}
                          {itinerary.favoriteCount > 0 && (
                            <span>{itinerary.favoriteCount}</span>
                          )}
                        </div>
                      </div>
                    </ItineraryContent>
                  </ItineraryCarouselCard>
                );
              })
            ) : (
              <ItineraryCarouselCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                onClick={() => {
                  setSelectedCountryFilter(country);
                  setFormData(prev => ({
                    ...prev,
                    place: country,
                    itineraryType: '',
                    budget: 'any',
                    days: 3
                  }));
                  setTimeout(() => {
                    const formElement = document.querySelector('form');
                    if (formElement) {
                      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                <ItineraryImage imageUrl={`https://images.unsplash.com/photo-${1500000000000 + (countryIndex * 1000)}?w=800&q=80&auto=format&fit=crop`}>
                  <AIBadgeSmall>
                    <SparklesIcon style={{ width: '12px', height: '12px' }} />
                    No Itineraries
                  </AIBadgeSmall>
                </ItineraryImage>
                <ItineraryContent>
                  <ItineraryCardTitle>Explore {country}</ItineraryCardTitle>
                  <ItineraryCardDescription>
                    No itineraries available yet. Click to create your first itinerary for {country}.
                  </ItineraryCardDescription>
                  <ItineraryCardMeta>
                    Click to create itinerary
                  </ItineraryCardMeta>
                </ItineraryContent>
              </ItineraryCarouselCard>
            )}
          </ItineraryCarouselCardsContainer>
          
          {displayItineraries.length > 0 && (
            <CarouselNextButton
              onClick={handleCarouselNext}
              disabled={!canScrollNext()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Next itineraries"
            >
              <ChevronRightIcon />
            </CarouselNextButton>
          )}
        </ItineraryCarouselContainer>
      )}
    </CountrySection>
  );
};

export default CountryItinerariesCarousel;

